/**
 * MCP (Model Context Protocol) admin/review surfaces:
 *  - draft clinical notes an MCP agent proposed, that a human already confirmed
 *  - the MCP tool-invocation audit trail (super-admin visibility)
 *  - a clinic's write-tools opt-in, set per clinic by a super admin (medopd-docs 131)
 *
 * Backend paths are under server.servlet.context-path=/api + the controller's own mapping —
 * e.g. McpDraftClinicalNoteController maps `/mcp/draft-clinical-notes`, reachable at
 * `/api/mcp/draft-clinical-notes`.
 */
import { fetchApi } from '../client';
import { buildQS } from '../helpers';
import type {
  McpDraftClinicalNote, McpAuditLog, McpAuditLogsPage, McpFeatureConfig,
} from '../../../types';

export const mcpDraftNotesModule = {
  listForPatient: (patientId: string) =>
    fetchApi<McpDraftClinicalNote[]>(
      `/api/mcp/draft-clinical-notes${buildQS({ patientId })}`
    ),
};

export const mcpAuditModule = {
  getLogs: (limit = 50, page = 0, clinicId?: string) =>
    fetchApi<McpAuditLog[] | McpAuditLogsPage>(
      `/api/mcp/audit-logs${buildQS({ limit, page, clinicId })}`
    ),
};

/** Super admin only, per clinic (medopd-docs 131): "let connected AI tools draft clinical notes for doctors to review". */
export const mcpSettingsModule = {
  get: (clinicId: string) =>
    fetchApi<McpFeatureConfig>(`/api/admin/clinics/${encodeURIComponent(clinicId)}/mcp-settings`),

  setWriteToolsEnabled: (clinicId: string, enabled: boolean) =>
    fetchApi<McpFeatureConfig>(`/api/admin/clinics/${encodeURIComponent(clinicId)}/mcp-settings/write-tools`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),
};
